import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Retry function with exponential backoff
const retryWithBackoff = (failureCount: number, error: any) => {
  // Don't retry on 4xx errors (client errors)
  if (error?.response?.status >= 400 && error?.response?.status < 500) {
    return false;
  }
  // Retry up to 2 times with exponential backoff
  return failureCount < 2;
};

// Configure React Query with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes (balance between freshness and performance)
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes (reduced from 10min to save memory)
      gcTime: 10 * 60 * 1000,
      // Retry failed requests with exponential backoff (max 2 retries)
      retry: retryWithBackoff,
      // Refetch on window focus for fresh data (can be disabled per-query)
      refetchOnWindowFocus: 'always',
      // Don't refetch on mount if data is fresh (prevents duplicate requests)
      refetchOnMount: false,
      // Don't refetch on reconnection (prevents storm of requests)
      refetchOnReconnect: false,
      // Use structural sharing for better performance
      structuralSharing: true,
      // Notify on change by default
      notifyOnChangeProps: 'all',
    },
    mutations: {
      // Retry failed mutations once (be conservative with mutations)
      retry: 1,
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

export default QueryProvider;
